import { useMutation, useQueryClient } from 'react-query';
import { useApiNoSqlContext } from 'src/modules/contexts/ApiNoSqlContext';
import { ApiError, CollectionNames, DeleteOneResponse } from 'src/modules/generated-api-clients/no-sql';
import { AxiosError } from 'axios';
import { logApiError, logAxiosError, logError, logRequest } from './helpers';
import { MUTATION_KEYS, QUERY_KEYS } from './enums';

/**
 * Custom hook for deleting a single document by collection name and document id.
 * Endpoint: DELETE '/api/v2/delete/{collection_name}/{id}'
 * @returns An object containing the response and error, if any.
 */
export const useMutationDeleteSingleFromCollection = () => {
	const { client } = useApiNoSqlContext();
	const queryClient = useQueryClient();
	type DeleteSingleDocumentByIdParams = {
		collectionName: CollectionNames;
		id: string;
	};

	return useMutation<any, ApiError, DeleteSingleDocumentByIdParams>(
		({ collectionName, id }: DeleteSingleDocumentByIdParams) => {
			if (!client) {
				throw new Error('API client is not initialized');
			}
			const response = client.delete.deleteOneDocumentByIdOr404(collectionName, id);
			logRequest({ collectionName, id }, 'DELETE', MUTATION_KEYS.delete.DeleteSingleFromCollectionById, response);
			return response;
		},
		{
			onSuccess: (data: DeleteOneResponse) => {
				if (data.collection === CollectionNames.PROMPTS) {
					queryClient.invalidateQueries(QUERY_KEYS.prompts.AllPrompts);
					queryClient.invalidateQueries(QUERY_KEYS.prompts.SinglePrompt);
				} else if (data.collection === CollectionNames.UTILITY_TEMPLATE) {
					queryClient.invalidateQueries(QUERY_KEYS.utilities.AllUtilities);
					queryClient.invalidateQueries(QUERY_KEYS.utilities.SingleUtility);
				} else if (data.collection === CollectionNames.STAGE_ONE_STAGE_TWO) {
					queryClient.invalidateQueries(QUERY_KEYS.invoices.AllInvoices);
					queryClient.invalidateQueries(QUERY_KEYS.invoices.SingleInvoice);
					queryClient.invalidateQueries(QUERY_KEYS.invoices.UniqueFieldOr404);
				}
				console.log('Delete single document by ID successful:', JSON.stringify(data));
			},
			onError: (error: unknown) => {
				if (error instanceof ApiError) {
					logApiError(error);
				} else if (error instanceof AxiosError) {
					logAxiosError(error);
				} else {
					logError(error, 'Error deleting single document from a specific collection by ID');
				}
				return;
			}
		}
	);
};

/**
 * Custom hook for deleting a document by customer ID, utility ID, and document ID.
 * Endpoint: DELETE '/api/v2/delete/{customer_id}/{utility_id}/{document_id}'
 * @returns An object containing the response and error, if any.
 */
export const useMutationDeleteDocumentByCustomerIdUtilityIdDocumentId = () => {
	const { client } = useApiNoSqlContext();
	const queryClient = useQueryClient();

	type DeleteDocumentByCustomerIdUtilityIdDocumentIdParams = {
		customerId: string;
		utilityId: string;
		documentId: string;
	};

	return useMutation<any, ApiError, DeleteDocumentByCustomerIdUtilityIdDocumentIdParams>(
		({ customerId, utilityId, documentId }: DeleteDocumentByCustomerIdUtilityIdDocumentIdParams) => {
			if (!client) {
				throw new Error('API client is not initialized');
			}
			const response = client.delete.deleteDeleteDbAndS3ById(
				customerId,
				utilityId,
				documentId
			);
			logRequest({}, 'DELETE', MUTATION_KEYS.delete.DeleteSingleDocumentById, response);
			return response;
		},
		{
			onSuccess: (data: any) => {
				console.log(
					'Delete document by customer ID, utility ID, and document ID successful:',
					JSON.stringify(data)
				);
				queryClient.invalidateQueries(QUERY_KEYS.invoices.AllInvoices);
				queryClient.invalidateQueries(QUERY_KEYS.invoices.SingleInvoice);
				queryClient.invalidateQueries(QUERY_KEYS.invoices.UniqueFieldOr404);
				queryClient.invalidateQueries(QUERY_KEYS.utilities.AllUtilities);
				queryClient.invalidateQueries(QUERY_KEYS.utilities.SingleUtility);
				queryClient.invalidateQueries(QUERY_KEYS.common.DocumentCount);
			},
			onError: (error: unknown) => {
				if (error instanceof ApiError) {
					logApiError(error);
				} else if (error instanceof AxiosError) {
					logAxiosError(error);
				} else {
					logError(error, 'Error deleting document by customer ID, utility ID, or document ID');
				}
				return;
			}
		}
	);
};
